import type SkuModel from 'volta/models/sku';
import { SkuResourceName, SkuEvents } from 'volta/models/sku';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';

import type AppSettings from 'volta/services/app-settings';
import type BvFlashService from 'volta/services/bv-flash';
import type BvMetricsService from 'volta/services/bv-metrics';
import type EventStreamService from 'volta/services/event-stream';

import type { TSkuDetailModalTab } from 'volta/services/sku-modal-service';
import type SkuModalService from 'volta/services/sku-modal-service';
import type RouterService from '@ember/routing/router-service';
import type { ISkuDetailsArgs } from './';

type TBomTab = 'descendants' | 'parents' | 'finishedProducts';

const TABS: TSkuDetailModalTab[] = [
  'settings',
  'planning',
  'execution',
  'stock',
  'transactions',
  'bom',
  'forecasts'
];

const DefaultTab: TSkuDetailModalTab = 'settings';

const BOM_TABS: TBomTab[] = ['descendants', 'parents', 'finishedProducts'];

export default class SkuDetailsContent extends Component<ISkuDetailsArgs> {
  @service router!: RouterService;
  @service bvMetrics!: BvMetricsService;
  @service appSettings!: AppSettings;
  @service skuModalService!: SkuModalService;
  @service eventStream!: EventStreamService;
  @service bvFlash!: BvFlashService;
  @service intl!: IntlService;

  @tracked selectedOpenOrderQuery?: object;
  @tracked showOpenOrders = false;

  @tracked selectedBomTab: TBomTab = 'descendants';
  @tracked showSimplifiedStockView: boolean = false;
  @tracked skuIsDirty: boolean = false;

  bomTabs = BOM_TABS;

  get options() {
    return this.args.options || {};
  }

  get selectedTab() {
    const { tab } = this.args;
    return TABS.includes(tab) ? tab : DefaultTab;
  }

  // Computed Properties
  // ~~~~~

  constructor(owner: any, args: ISkuDetailsArgs) {
    super(owner, args);
    this.initTabRoute();

    this.showSimplifiedStockView = Boolean(
      this.appSettings.features?.workshop.simplifiedWorkshopCapacity
    );
    this.eventStream.addHandler(
      SkuResourceName.singular,
      SkuEvents.SkuDeactivated,
      this._onActivationSuccess,
      this
    );
  }

  @action
  toggleStockView() {
    this.bvMetrics.trackAction(
      this,
      'toggleStockView',
      `${this.showSimplifiedStockView ? 'simple' : 'expert'}`
    );
    this.showSimplifiedStockView = !this.showSimplifiedStockView;
  }

  @action
  transitionToSkuDetails() {
    this.bvMetrics.trackAction(this, 'transitionToSkuDetails');
    this.hideOpenOrdersModal();
    this.router.transitionTo('protected.skus.sku', this.args.skuId);
  }

  @action
  hideOpenOrdersModal() {
    this.bvMetrics.trackAction(this, 'hideOpenOrdersModal');
    this.selectedOpenOrderQuery = undefined;
    this.showOpenOrders = false;
  }

  @action
  showOpenOrdersModal(query: object) {
    this.bvMetrics.trackAction(this, 'showOpenOrdersModal');
    this.selectedOpenOrderQuery = query;
    this.showOpenOrders = true;
  }

  @action
  onTabChange(tab: TSkuDetailModalTab) {
    if (!typeof this.args.switchTab) {
      this.initTabRoute(tab);
    }
  }

  @action
  switchTab(tab: TSkuDetailModalTab) {
    this.initTabRoute(tab);
  }

  @action
  async activateSku() {
    const { sku } = this.args;
    if (sku) {
      try {
        await sku.activateSku(false);
      } catch (e) {
        this._onActivationFailure(e);
      }
    }
  }

  @action
  initTabRoute(tab?: TSkuDetailModalTab) {
    if(typeof this.args.switchTab === 'function') {
      this.args.switchTab(tab || this.selectedTab);
    } else if (tab) {
      this.router.transitionTo({ queryParams: { tab } });
    } else if (this.selectedTab !== this.args.tab) {
      this.router.transitionTo({ queryParams: { tab: this.selectedTab } });
    }
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
  }

  private _onActivationSuccess(result: SkuModel) {
    this.bvFlash.success(
      this.intl.t('bvList.singleActions.updateSuccess', {
        resourceNameSingular: this.intl.t(SkuResourceName.singular),
        name: result.partCode
      })
    );
  }

  private _onActivationFailure(error: string) {
    this.bvFlash.error(error);
  }
}
